import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusTooltipModule } from '@trimble-gcs/modus';
import { interval } from 'rxjs';
import { ScandataModel } from '../../scandata/scandata.models';
import { ingestionProgressTranslations } from './ingestion-progress.translations';

interface Progress {
  tooltip: string;
  percentageComplete: number;
}

@UntilDestroy()
@Component({
  selector: 'sd-ingestion-progress',
  imports: [CommonModule, ModusTooltipModule, MatProgressSpinnerModule],
  templateUrl: './ingestion-progress.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngestionProgressComponent {
  private readonly translationService = inject(TranslationService);
  private readonly translations = ingestionProgressTranslations();

  // signals
  private readonly updateDisplayInterval = toSignal(interval(1500));

  // inputs
  readonly scan = input.required<ScandataModel>();

  // computes
  readonly progress = computed(() => {
    this.updateDisplayInterval();
    return this.getProgress(this.scan());
  });

  private getProgress(scan: ScandataModel): Progress {
    if (isNil(scan.ingestionStartedDate) || isNil(scan.ingestionEstimatedCompleteDate))
      return { tooltip: this.translations.tooltip.queued.text, percentageComplete: 0 };

    const nowTime = Date.now();
    const startTime = scan.ingestionStartedDate.getTime();
    const finishTime = scan.ingestionEstimatedCompleteDate.getTime();
    const minutesRemaining = Math.ceil((finishTime - nowTime) / 60000);

    const totalTime = finishTime - startTime;
    const elapsedTime = Math.ceil(nowTime - startTime);
    const elapsedPercentage = Math.floor((elapsedTime / totalTime) * 100);
    const percentageComplete = elapsedPercentage > 99 ? 99 : elapsedPercentage;

    return this.getProgressTooltipFormatted(percentageComplete, minutesRemaining);
  }

  private getProgressTooltipFormatted(
    percentageComplete: number,
    minutesRemaining: number,
  ): Progress {
    const tooltipLine1 = this.translationService.translate(
      this.translations.tooltip.progress.percentageComplete.text,
      { percentageComplete },
    );

    const remaining =
      minutesRemaining <= 1
        ? this.translations.minutesRemaining.lessThanMinute.text
        : this.translationService.translate(this.translations.minutesRemaining.minutes.text, {
            minutes: minutesRemaining,
          });

    const tooltipLine2 = this.translationService.translate(
      this.translations.tooltip.progress.minutesRemaining.text,
      { minutesRemaining: remaining },
    );

    return {
      tooltip: `${tooltipLine1}\n${tooltipLine2}`,
      percentageComplete,
    };
  }
}
